<!-- airsim add 2026.06.07 -->
<!--
  우하단 통합 런처(Groware Pro  로고) — 채널톡 기본 버튼은 숨기고(plugins/channelTalk.js) 이 런처 하나만 노출.
  런처 클릭 → 미니 팝업(메뉴얼 보기 / 문의하기)을 먼저 띄운다.
  - 메뉴얼 보기 : Oopy 발행 가이드 사이트를 패널 안 iframe 으로 임베드
  - 문의하기   : 채널톡 메신저를 프로그램으로 오픈 ChannelIO('showMessenger')
  - 고려 사항 : 우피를 iframe 으로 띄우면 우피에도 채널톡이 연결 되어 있어서 UX 이상해짐 ( 우피에서 채널톡 링크 제거  )
-->
<template>
  <div class="manual-widget">
    <!-- 메뉴얼 패널 (iframe) -->
    <transition name="manual-widget-panel">
      <section
        v-show="manualOpen"
        class="manual-widget__panel"
        role="dialog"
        :aria-label="$tc('메뉴얼.타이틀')"
      >
        <header class="manual-widget__header">
          <span class="manual-widget__title">{{ $tc("메뉴얼.타이틀") }}</span>
          <div class="manual-widget__actions">
            <button
              type="button"
              class="manual-widget__action"
              :title="$tc('메뉴얼.새탭으로_열기')"
              :aria-label="$tc('메뉴얼.새탭으로_열기')"
              @click="openInNewTab"
            >
              <base-svg name="icon-newtab" />
            </button>
          </div>
        </header>
        <div class="manual-widget__body">
          <iframe
            v-if="frameMounted"
            class="manual-widget__frame"
            :src="manualUrl"
            :title="$tc('메뉴얼.타이틀')"
          ></iframe>
        </div>
      </section>
    </transition>

    <!-- 통합 메뉴 팝업: 메뉴얼 보기 / 문의하기 -->
    <transition name="manual-widget-panel">
      <div v-show="menuOpen" class="manual-widget__menu" role="menu">
        <button
          type="button"
          class="manual-widget__menu-item"
          role="menuitem"
          @click="openManual"
        >
          <base-svg name="icon-manual" />
          <span>{{ $tc("메뉴얼.메뉴얼보기") }}</span>
        </button>
        <button
          type="button"
          class="manual-widget__menu-item"
          role="menuitem"
          @click="openInquiry"
        >
          <base-svg name="icon-inquiry" />
          <span>{{ $tc("메뉴얼.문의하기") }}</span>
        </button>
      </div>
    </transition>

    <!-- GroupwarePro 로고 → 클릭 시 메뉴 팝업 -->
    <button
      v-show="!ctOpen"
      type="button"
      class="manual-widget__launcher"
      :class="{ 'is-open': isOpen }"
      :title="$tc('메뉴얼.툴팁')"
      :aria-label="$tc('메뉴얼.툴팁')"
      :aria-expanded="isOpen ? 'true' : 'false'"
      @click="toggleLauncher"
    >
      <base-svg :name="isOpen ? 'icon-close' : 'icon-gpro-mark'" />
    </button>
  </div>
</template>

<script>
import { MANUAL_GUIDE_URL } from "@/config/externalSites";

export default {
  name: "ManualWidget",
  data() {
    return {
      menuOpen: false,
      manualOpen: false,
      // iframe 은 메뉴얼을 최초 1회 열었을 때만 생성 → 진입 시 미리 로드하지 않는다.
      frameMounted: false,
      // airsim add 2026.06.08 : 채널톡 메신저가 열려 있는 동안 true → 런처 숨김(CT 닫기 X와 겹쳐 보이는 것 방지)
      ctOpen: false,
    };
  },
  computed: {
    manualUrl() {
      return MANUAL_GUIDE_URL;
    },
    // 런처가 열림 상태(메뉴 또는 패널)인지 → X 아이콘/닫기 동작 판단
    isOpen() {
      return this.menuOpen || this.manualOpen;
    },
  },
  // airsim add 2026.06.08 : 채널톡 메신저 열림/닫힘 감지 → GPRO 런처 표시 토글(비침 방지)
  mounted() {
    if (window.ChannelIO) {
      window.ChannelIO("onShowMessenger", () => {
        this.ctOpen = true;
      });
      window.ChannelIO("onHideMessenger", () => {
        this.ctOpen = false;
      });
    }
  },
  methods: {
    toggleLauncher() {
      // 채널톡처럼 런처 버튼 하나로 열고/닫기 (열려 있으면 닫고, 아니면 메뉴 열기)
      if (this.manualOpen) {
        this.manualOpen = false;
      } else if (this.menuOpen) {
        this.menuOpen = false;
      } else {
        this.menuOpen = true;
      }
    },
    openManual() {
      this.menuOpen = false;
      this.manualOpen = true;
      this.frameMounted = true;
    },
    openInquiry() {
      this.menuOpen = false;
      // 채널톡 메신저 오픈 (기본 버튼은 숨겨두고 여기서 프로그램으로 호출)
      if (window.ChannelIO) {
        window.ChannelIO("showMessenger");
      }
    },
    openInNewTab() {
      window.open(this.manualUrl, "_blank", "noopener,noreferrer");
    },
  },
};
</script>

<style lang="scss" scoped>
$manual-launcher-size: 56px; // 런처 한 변 길이 (둥근 사각형, 채널톡 과 유사하게 )
$manual-edge-gap: 16px; // 화면 우/하단 가장자리 여백 (채널톡 버튼 16px에 맞춤)
$manual-launcher-bottom: 16px; // 런처를 화면 바닥에서 띄우는 거리 (채널톡 과 유사하게 )
$manual-panel-width: 400px; // 가이드 패널 가로 폭 (데스크탑 기준)
$manual-popup-bottom: $manual-launcher-bottom + $manual-launcher-size + 8px; // 메뉴/패널 바닥 기준 = 런처 위 8px (16+52+8 = 76px, 채널톡 과 맞춤 )

.manual-widget {
  &__launcher {
    position: fixed;
    right: $manual-edge-gap;
    bottom: $manual-launcher-bottom;
    // 패널(+1)보다 위 → 채널톡처럼 X(닫기)가 항상 떠 있게
    z-index: $gpro-header-z-index + 2;
    display: flex;
    align-items: center;
    justify-content: center;
    width: $manual-launcher-size;
    height: $manual-launcher-size;
    padding: 0;
    border: 0;
    border-radius: 25px; // 채널톡 버튼 라운드 와 유사하게
    background: $--color-primary;
    color: $--color-white;
    cursor: pointer;
    transition: transform 0.15s ease, box-shadow 0.15s ease;
    @include bs-shadow("medium");

    &:hover {
      transform: translateY(-2px);
    }

    .base-svg {
      color: $--color-white;
    }
  }

  // 메뉴 팝업 (메뉴얼 보기 / 문의하기)
  &__menu {
    position: fixed;
    right: $manual-edge-gap;
    bottom: $manual-popup-bottom;
    z-index: $gpro-header-z-index + 1;
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 200px;
    max-width: calc(100vw - #{$manual-edge-gap * 2});
    padding: 12px;
    border-radius: 12px;
    background: $--color-white;
    @include bs-shadow("medium");
  }

  &__menu-item {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    height: 44px;
    padding: 0 14px;
    border: 0;
    border-radius: 8px;
    background: $gpro-warm-gray-900;
    color: $--color-white;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: opacity 0.15s ease;

    .base-svg {
      color: $--color-white;
    }

    &:hover {
      opacity: 0.85;
    }
  }

  // 메뉴얼 패널
  &__panel {
    position: fixed;
    right: $manual-edge-gap;
    bottom: $manual-popup-bottom;
    z-index: $gpro-header-z-index + 1; // 해더 보다 1 위에
    display: flex;
    flex-direction: column;
    width: $manual-panel-width;
    max-width: calc(100vw - #{$manual-edge-gap * 2});
    height: 640px;
    max-height: calc(100vh - #{$manual-popup-bottom + 12px});
    overflow: hidden;
    border-radius: 12px;
    background: $--color-white;
    @include bs-shadow("medium");
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex: 0 0 auto;
    height: 48px;
    padding: 0 8px 0 16px;
    border-bottom: $gpro-border-light;
  }

  &__title {
    font-size: 14px;
    font-weight: 600;
    color: $--color-text-primary;
  }

  &__actions {
    display: flex;
    align-items: center;
  }

  &__action {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    border: 0;
    border-radius: 6px;
    background: transparent;
    color: $--color-text-secondary;
    cursor: pointer;
    transition: background 0.15s ease, color 0.15s ease;

    &:hover {
      background: $gpro-warm-gray-100;
      color: $--color-text-primary;
    }
  }

  &__body {
    flex: 1 1 auto;
    min-height: 0;
    background: $gpro-warm-gray-50;
  }

  &__frame {
    display: block;
    width: 100%;
    height: 100%;
    border: 0;
  }
}

// 팝업/패널 열림·닫힘 트랜지션 (채널톡 체감에 맞춘 0.3s )
.manual-widget-panel-enter-active,
.manual-widget-panel-leave-active {
  transition: opacity 0.3s ease-out, transform 0.3s ease-out;
}
.manual-widget-panel-enter,
.manual-widget-panel-leave-to {
  opacity: 0;
  transform: translateY(12px);
}

// 모바일/좁은 화면: 패널을 거의 전체 화면으로 ( 조금이라도 더 많은 정보를 보여주기 위해 )
@media (max-width: 480px) {
  .manual-widget__panel {
    right: 12px;
    left: 12px;
    bottom: 12px;
    top: 12px;
    width: auto;
    max-width: none;
    height: auto;
    max-height: none;
  }
}
</style>
